import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { authService } from '@/services/auth'

function EmailVerificationPage() {
  const [searchParams] = useSearchParams()
  const [isVerifying, setIsVerifying] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Check if we have a token in the URL (for email verification)
  const token = searchParams.get('token')
  const type = searchParams.get('type')
  
  useEffect(() => {
    // If we have a token and it's a verification token, verify the email
    if (token && type === 'verification') {
      const verifyEmail = async () => {
        try {
          setIsVerifying(true)
          const { error } = await authService.verifyEmail(token)
          
          if (error) {
            setError(error)
            return
          }
          
          setSuccess(true)
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to verify email')
        } finally {
          setIsVerifying(false)
        }
      }
      
      verifyEmail()
    }
  }, [token, type])
  
  // If no token, show instructions to check email
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md p-6 space-y-6 bg-background rounded-xl shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text">Check Your Email</h1>
            <p className="text-text/70 mt-2">
              We've sent you an email with a verification link. Please check your inbox and click the link to verify your account.
            </p>
          </div>
          
          <div className="text-sm text-text/70">
            <p>If you don't see the email in your inbox, please check your spam folder.</p>
            <p className="mt-2">
              Still can't find it?{' '}
              <button 
                className="text-primary hover:underline"
                onClick={async () => {
                  const { error } = await authService.resendVerificationEmail()
                  if (error) {
                    setError(error)
                  } else {
                    alert('Verification email has been resent!')
                  }
                }}
              >
                Resend verification email
              </button>
            </p>
          </div>
          
          <div className="text-center text-sm text-text/70">
            <Link to="/login" className="text-primary hover:underline">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    )
  }
  
  // If verifying, show loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md p-6 space-y-6 bg-background rounded-xl shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text">Verifying Your Email</h1>
            <p className="text-text/70 mt-2">
              Please wait while we verify your email address...
            </p>
          </div>
        </div>
      </div>
    )
  }
  
  // If error, show error message
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md p-6 space-y-6 bg-background rounded-xl shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text">Verification Failed</h1>
            <p className="text-red-500 mt-2">
              {error}
            </p>
          </div>
          
          <div className="text-center text-sm text-text/70">
            <Link to="/login" className="text-primary hover:underline">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    )
  }
  
  // If verified, show success message
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md p-6 space-y-6 bg-background rounded-xl shadow-lg">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text">Email Verified</h1>
          <p className="text-green-500 mt-2">
            {success 
              ? "Your email has been successfully verified. You can now log in to your account."
              : "Your email verification is complete. You can now log in to your account."
            }
          </p>
        </div>
        
        <div className="text-center">
          <Link 
            to="/login" 
            className="inline-block py-2 px-4 bg-primary hover:bg-primary-dark text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    </div>
  )
}

export { EmailVerificationPage }
